import React, { useEffect, useRef, useState } from "react"
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Icon,
  Flex,
  InputGroup,
  InputRightElement,
  Link,
} from "@chakra-ui/react"
import { EditIcon } from "@chakra-ui/icons" // Importing an edit icon
import { WalletSelector } from "../components/WalletSelector"
import { useAppContext } from "../AppContext"
import { formatSolBalance } from "../utils"
import { QuoteService, TokenInfoResp, TokenService } from "../api"
import { TradeService } from "../api/trade"

export const TokenSwap: React.FC = () => {
  // here begins slippage
  const DEFAULT_SLIPPAGE = 20
  const DEFAULT_OUTPUT_SLIPPAGE = 40
  const [slippageModal, setSlippageModal] = useState<number>(DEFAULT_SLIPPAGE) // Default slippage
  const [isSlippageOpen, setIsSlippageOpen] = useState<boolean>(false)
  const [slippage, setSlippage] = useState<number>(DEFAULT_SLIPPAGE)
  const [outSlippage, setOutSlippage] = useState<number>(DEFAULT_OUTPUT_SLIPPAGE)
  const [outModalSlippage, setOutModalSlippage] = useState<number>(DEFAULT_OUTPUT_SLIPPAGE)
  const [isOutSlippageOpen, setIsOutSlippageOpen] = useState<boolean>(false)

  const [isTokenModalOpen, setIsTokenModalOpen] = useState<boolean>(false)
  const [selectedToken, setSelectedToken] = useState<TokenInfoResp | null>(null) // Token being displayed out of modal
  const [tokenMetadata, setTokenMetadata] = useState<TokenInfoResp | null>(null) // Token metadata in the modal
  const [holding, setHolding] = useState<string>("0")
  const [holdingValue, setHoldingValue] = useState<string>("0")
  const [publicKey, setPublicKey] = useState<string>("")
  const [lastPublicKey, setLastPublicKey] = useState<string>("") // Track last successful public key
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null)
  const [outputAmount, setOutputAmount] = useState<string>("")

  // here begins interval duration
  const [intervalDuration, setIntervalDuration] = useState<number>(5000) // Default interval duration
  const [intervalEdit, setIntervalEdit] = useState<number>(5000) // Default interval duration
  const [intervalModal, setIntervalModal] = useState<boolean>(false) // Default interval duration

  // here begins interval handlers
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const holdingInterval = useRef<NodeJS.Timeout | null>(null)

  // here begin the biz params
  const [inSwaping, setInSwaping] = useState<boolean>(false)
  const [readyKey, setReadyKey] = useState<string>("")
  const [inputAmount, setInputAmount] = useState<string>("")
  const { activeWallet } = useAppContext()

  // here begin res data
  const [tx, setTx] = useState<string[]>([])

  const preSwapCheck = async () => {
    if (!activeWallet) {
      return false
    }

    if (!inputAmount || parseFloat(inputAmount) === 0) {
      return false
    }

    if (!readyKey) {
      return false
    }
  }

  const handleSwap = async () => {
    if (!preSwapCheck()) {
      return
    }

    console.log(`Initiated swap!`)
    setInSwaping(true)

    TradeService.swapToTarget({
      inputAmount: inputAmount,
      outputMint: readyKey,
      config: {
        slippage: slippage / 100,
      },
    })
      .then(res => {
        console.log(`Swap result: ${res}`)
        setTx(res)
      })
      .catch(err => {
        console.log(`Swap error: ${err}`)
      })
      .finally(() => {
        setInSwaping(false)
      })
  }

  const preDumpChecek = async () => {
    if (!activeWallet) {
      return false
    }

    if (!readyKey) {
      return false
    }
  }

  const handleDump = async (p: number) => {
    if (!preDumpChecek()) {
      return false
    }

    console.log(`Initiated swap!`)
    setInSwaping(true)
    TradeService.swapFromTargetByPercent({
      inputMint: readyKey,
      p,
      config: {
        slippage: outSlippage / 100,
      },
    })
      .then(res => {
        console.log(`Swap result: ${res}`)
        setTx(res)
      })
      .catch(err => {
        console.log(`Swap error: ${err}`)
      })
      .finally(() => {
        setInSwaping(false)
      })
  }

  // Update output quote
  useEffect(() => {
    setTimeout(() => getQuoteHandler())
    const handler = setInterval(async () => {
      await getQuoteHandler()
    }, intervalDuration)

    console.log(`load with handler: ${handler}`)
    intervalRef.current = handler

    return () => {
      console.log(`clear interval handle: ${intervalRef.current}`)
      clearInterval(intervalRef.current!)
    }
  }, [inputAmount, activeWallet, readyKey, intervalDuration])
  const getQuoteHandler = async () => {
    if (inputAmount && parseFloat(inputAmount) !== 0 && activeWallet && readyKey) {
      QuoteService.getQuote(readyKey, inputAmount)
        .then(output => {
          console.log(`Output info: ${output}`)
          setOutputAmount(output.output)
        })
        .catch(err => {
          console.error(err)
        })
    } else {
      console.log(`interval skipped: ${inputAmount}:${JSON.stringify(activeWallet)}:${readyKey}`)
      setOutputAmount("")
    }
  }

  // Update token holding
  useEffect(() => {
    setTimeout(() => getHoldingHandler())
    const handler = setInterval(async () => {
      await getHoldingHandler()
    }, intervalDuration)

    console.log(`load with handler: ${handler}`)
    holdingInterval.current = handler

    return () => {
      console.log(`clear interval handle: ${holdingInterval.current}`)
      clearInterval(holdingInterval.current!)
    }
  }, [readyKey, activeWallet])
  const getHoldingHandler = async () => {
    if (readyKey && activeWallet) {
      try {
        const holding = await TokenService.getTokenHolding(readyKey, activeWallet.identifier)
        setHolding(holding.uiAmount)
        if (parseFloat(holding.uiAmount) > 0) {
          const hv = await QuoteService.getHoldingValue(readyKey, activeWallet.identifier)
          setHoldingValue(hv.output)
        }
      } catch (err) {
        console.error(err)
      }
    } else {
      console.log(`interval skipped: ${inputAmount}:${JSON.stringify(activeWallet)}`)
    }
  }

  const resetInterval = (duration: number) => {
    // console.log(`clear interval handle: ${intervalRef.current}`);
    // console.log(`set new interval: ${duration}`);
    // clearInterval(intervalRef.current!);
    // const handler = setInterval(async () => {
    //   await getQuoteHandler();
    // }, duration);

    setIntervalDuration(duration)
    // intervalRef.current = handler;
    setIntervalModal(false)
  }

  const submitSlippage = () => {
    setIsSlippageOpen(false)
    setSlippage(slippageModal)
  }

  const submitOutSlippage = () => {
    setIsOutSlippageOpen(false)
    setOutSlippage(outModalSlippage)
  }

  const handlePublicKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setPublicKey(value)
    setTokenMetadata(null) // Clear metadata while typing

    // Clear previous timeout
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    // If the input is not empty, set a new timeout
    if (value) {
      const id = setTimeout(async () => {
        // Only perform lookup if the current input is different from the last successful public key
        if (lastPublicKey !== value) {
          TokenService.getMetadata(value)
            .then(token => {
              setTokenMetadata(token)
              setLastPublicKey(value) // Update last successful public key
            })
            .catch(err => {
              console.error(err)
              setTokenMetadata(null) // Clear metadata if not found
            })
        }
      }, 1000) // 1 second

      setTimeoutId(id)
    } else {
      // Reset last public key if the input is cleared
      setLastPublicKey("")
    }
  }

  const handlePublicKeyBlur = async () => {
    // Check immediately on blur if the input is not empty
    if (publicKey && lastPublicKey !== publicKey) {
      const token = await TokenService.getMetadata(publicKey)
      if (token) {
        setTokenMetadata(token)
        setLastPublicKey(publicKey) // Update last successful public key
      } else {
        setTokenMetadata(null) // Clear metadata if not found
      }
    }
  }

  const handleSubmitPublicKey = () => {
    // Close the modal after submitting the public key
    setReadyKey(publicKey)
    setIsTokenModalOpen(false)
    setSelectedToken(tokenMetadata)
  }

  return (
    <Flex flexDirection={"column"} width={"100%"}>
      <Box width={"100%"} borderWidth="1px" borderRadius="lg" padding="6" boxShadow="lg" backgroundColor="white" mx="auto">
        <Heading size="lg" textAlign="center" mb="6">
          Swap Tokens
        </Heading>

        <Stack spacing={4}>
          <FormControl>
            <FormLabel htmlFor="inputAmount">Amount</FormLabel>
            <InputGroup>
              <Input id="inputAmount" placeholder="0.0" value={inputAmount} onChange={e => setInputAmount(e.target.value)} />
              {activeWallet && (
                <InputRightElement width={"60px"}>
                  <Text fontSize="sm" color="gray.500">
                    /{formatSolBalance(activeWallet.balance)}
                  </Text>
                </InputRightElement>
              )}
            </InputGroup>
          </FormControl>

          <FormControl>
            <FormLabel htmlFor="outputAmount">You will receive</FormLabel>
            <Input id="outputAmount" placeholder="0.0" value={outputAmount} isDisabled />
          </FormControl>

          <FormControl>
            <FormLabel htmlFor="publicKeyInput">Output Token Public Key</FormLabel>
            <Input
              id="publicKeyInput"
              placeholder="Enter public key"
              onClick={() => setIsTokenModalOpen(true)}
              isReadOnly // Make the input non-editable
              value={selectedToken ? `${selectedToken.name} (${selectedToken.symbol})` : ""}
            />
          </FormControl>

          <Text textAlign="center" fontSize="sm">
            Your Balance: {holding || "0"}
          </Text>
          <Text textAlign="center" fontSize="sm">
            Holding Value: {holdingValue || "0"}
          </Text>

          <Button colorScheme="blue" size="lg" width="full" onClick={handleSwap} isLoading={inSwaping}>
            Swap
          </Button>

          <Flex direction={"row"} justifyContent={"space-between"}>
            <Button colorScheme="blue" isLoading={inSwaping} onClick={() => handleDump(10)}>
              D10
            </Button>
            <Button colorScheme="blue" isLoading={inSwaping} onClick={() => handleDump(30)}>
              D30
            </Button>
            <Button colorScheme="blue" isLoading={inSwaping} onClick={() => handleDump(50)}>
              D50
            </Button>
            <Button colorScheme="blue" isLoading={inSwaping} onClick={() => handleDump(100)}>
              D100
            </Button>
          </Flex>

          <HStack justifyContent="space-between" mt={4}>
            <Text textAlign="center">Input Slippage: {slippage}%</Text>
            <Icon as={EditIcon} boxSize={5} cursor="pointer" onClick={() => setIsSlippageOpen(true)} />
          </HStack>

          <HStack justifyContent="space-between" mt={0}>
            <Text textAlign="center">Output Slippage: {outSlippage}%</Text>
            <Icon as={EditIcon} boxSize={5} cursor="pointer" onClick={() => setIsOutSlippageOpen(true)} />
          </HStack>

          <HStack justifyContent="space-between">
            <Text textAlign="center">Interval: {intervalDuration} ms</Text>
            <Icon
              as={EditIcon}
              boxSize={5}
              cursor="pointer"
              onClick={() => setIntervalModal(true)} // Open interval modal
            />
          </HStack>
        </Stack>

        {/* Input Slippage Modal */}
        <Modal isOpen={isSlippageOpen} onClose={() => setIsSlippageOpen(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit Slippage</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl>
                <FormLabel htmlFor="slippageInput">Slippage Percentage</FormLabel>
                <Input
                  id="slippageInput"
                  type="number"
                  value={slippageModal}
                  onChange={e => setSlippageModal(parseFloat(e.target.value))}
                  placeholder="Enter slippage percentage"
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={() => submitSlippage()}>
                Save
              </Button>
              <Button variant="ghost" onClick={() => setIsSlippageOpen(false)}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Output Slippage Modal */}
        <Modal isOpen={isSlippageOpen} onClose={() => setIsSlippageOpen(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit Slippage</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl>
                <FormLabel htmlFor="slippageInput">Slippage Percentage</FormLabel>
                <Input
                  id="slippageInput"
                  type="number"
                  value={outModalSlippage}
                  onChange={e => setOutModalSlippage(parseFloat(e.target.value))}
                  placeholder="Enter slippage percentage"
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={() => submitOutSlippage()}>
                Save
              </Button>
              <Button variant="ghost" onClick={() => setIsOutSlippageOpen(false)}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Interval Modal */}
        <Modal isOpen={intervalModal} onClose={() => setIntervalModal(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit Interval Duration</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl>
                <FormLabel htmlFor="intervalInput">Interval Duration (ms)</FormLabel>
                <Input
                  id="intervalInput"
                  type="number"
                  value={intervalEdit}
                  onChange={e => setIntervalEdit(parseInt(e.target.value))}
                  placeholder="Enter interval duration"
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={() => resetInterval(intervalEdit)}>
                Save
              </Button>
              <Button variant="ghost" onClick={() => setIntervalModal(false)}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Token Input Modal */}
        <Modal isOpen={isTokenModalOpen} onClose={() => setIsTokenModalOpen(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Input Public Key for Output Token</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl>
                <FormLabel htmlFor="publicKeyInput">Public Key</FormLabel>
                <Input
                  id="publicKeyInput"
                  value={publicKey}
                  onChange={handlePublicKeyChange}
                  onBlur={handlePublicKeyBlur} // Trigger on blur
                  placeholder="Enter public key"
                />
              </FormControl>
              {tokenMetadata && (
                <Text mt={4}>
                  Token Name: {tokenMetadata.name} <br />
                  Token Symbol: {tokenMetadata.symbol}
                </Text>
              )}
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={handleSubmitPublicKey}>
                Submit
              </Button>
              <Button variant="ghost" onClick={() => setIsTokenModalOpen(false)}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
      {tx && (
        <Box width={"100%"} borderWidth="1px" borderRadius="lg" padding="6" boxShadow="lg" backgroundColor="white" mx="auto" mt={2}>
          <Stack spacing={4}>
            {tx.map((link, index) => (
              <Link
                key={index}
                href={link}
                isExternal
                color="teal.500" // Change the color to teal
                fontWeight="bold" // Make the font bold
                textDecoration="underline" // Underline the link
                _hover={{ color: "teal.300", textDecoration: "none" }} // Hover effect
                _focus={{ boxShadow: "outline" }} // Focus effect
              >
                Solana Explorer: #{index + 1} {/* Start from 1 for better UX */}
              </Link>
            ))}
          </Stack>
        </Box>
      )}
      <WalletSelector />
    </Flex>
  )
}
